import React, { useState } from "react";
import { downcaseDomain, stripSchema } from "../../../utils/WebsiteUtil";
import { CSVErrorProps, NamedEntryProps } from "./EditObjectPools";
import { validateObject } from "../../../utils/Validation";
import { NamedEntry, ObjectPool } from "../../../types/Objects";
import { Box, Button, FileUpload, Flex, InlineNotification, Modal, Text, useTranslation } from "@familyzone/component-library";
import { useObjectStore } from "../../../storez/ObjectStore";

interface ImportCSVModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (listEntries: NamedEntryProps[]) => void;
  object: ObjectPool | undefined;
}

const ImportCSVModal: React.FC<ImportCSVModalProps> = ({ open, onClose, onImport, object }) => {
  const [loaded, setLoaded] = useState(true);
  const [CSVErrors, setCSVErrors] = useState<CSVErrorProps[]>([]);
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const saveObject = useObjectStore((state) => state.saveObject);

  const handleUpload = (file: File, done: (success: boolean) => void) => {
    setError("");
    setCSVErrors([]);
    if (object === undefined) {
      setError("No object selected");
      done(true);
      return;
    }

    setLoaded(false);
    const reader = new FileReader();

    const new_list_entries: NamedEntryProps[] = [];
    const lineErrors: CSVErrorProps[] = [];
    reader.readAsText(file);
    reader.onload = () => {
      const csv = reader.result?.toString();
      const lines = csv?.split("\n");
      if (!lines) {
        setLoaded(true);
        return;
      }
      if (lines.length > 10000) {
        setError("CSV file must not be more than 10000 lines");
        setLoaded(true);
        return;
      }

      lines?.forEach((line, index) => {
        line = line.trim();
        const values = line.split(",");
        if (values.length === 1) {
          if (values[0] === "") {
            return;
          } else {
            lineErrors.push({
              line: index + 1,
              error: "CSV Format does not match. Please follow the format in the example template",
              entry: values[0],
            });
          }
        }
        if (values.length === 3) {
          const preparedLine = stripSchema(values[2]);

          if (values[2] === "") {
            return;
          }

          if (values[0] === "Name" && values[1] === "Description" && values[2] === "Entry") {
            return;
          }

          const error = validateObject(preparedLine, object);
          if (error) {
            lineErrors.push({
              line: index + 1,
              error: error,
              entry: values[2],
            });
            return;
          }
          if (new_list_entries.find((value) => value.value === preparedLine)) {
            lineErrors.push({
              line: index + 1,
              error: "Entry already exists",
              entry: values[2],
            });
            return;
          }
          if (values[0] === "" || values[1] === "") {
            new_list_entries.push({
              name: values[0] === "" ? preparedLine : values[0],
              desc: values[1] === "" ? (values[1] !== "" ? values[1] : "") : values[1],
              value: preparedLine,
              click_action: "edit",
              invalidValueMsg: null,
              id: index,
            });
          } else {
            new_list_entries.push({
              name: values[0],
              desc: values[1],
              value: preparedLine,
              click_action: "edit",
              invalidValueMsg: null,
              id: index,
            });
          }
        } else {
          lineErrors.push({
            line: index + 1,
            error: "CSV Format does not match. Please follow the format in the example template",
            entry: values[0],
          });
        }
      });
      if (lineErrors.length > 0) {
        setCSVErrors(lineErrors);
        setLoaded(true);
        done(true);
        return;
      }
      if (new_list_entries.length === 0) {
        setLoaded(true);
        setError("No valid entries found in CSV");
        done(true);
        return;
      }

      const newObject = object;
      newObject.named_entries = new_list_entries;

      if (newObject.type === 2) {
        newObject.named_entries = newObject.named_entries.map((value) => ({
          ...value,
          value: downcaseDomain(value.value),
        }));
      }

      setLoaded(false);
      newObject.named_entries.forEach((entry: NamedEntry) => {
        delete entry.id;
      });

      void (async () => {
        await saveObject(newObject);
        onImport(new_list_entries);
        // Only after saveObject, set loaded to true and call callback
        setLoaded(true);
        done(true);
      })();
    };
  };

  const handleDownloadErrors = () => {
    const headers = ["Line", "Error Message", "Entry"];
    const csv = CSVErrors.map((error) => `${error.line},${error.error},${error.entry}`).join("\n");
    const csvData = [headers, csv].join("\n");
    const element = document.createElement("a");
    const file = new Blob([csvData], { type: "text/csv" });
    element.href = URL.createObjectURL(file);
    element.download = "errors.csv";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  // Add a handler for closing the error notification
  const handleCloseNotice = () => {
    setCSVErrors([]);
    setError(null);
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      headerText="Import CSV"
      contentProps={{ style: { maxHeight: "700px" } }}
      size={CSVErrors.length > 0 ? "md" : "sm"}
    >
      <Box>
        <Flex direction="column" pt="sp16" pb="sp16">
          {error && CSVErrors.length === 0 && (
            <Flex pt="sp24" pr="sp24" pl="sp24">
              <InlineNotification
                notificationTitle="CSV Import Error"
                onCloseButtonClick={handleCloseNotice}
                status="error"
                notificationDescription={
                  <Flex direction="column">
                    <Text>{error}</Text>
                  </Flex>
                }
              />
            </Flex>
          )}
          <Flex pb="sp24">
            <InlineNotification
              status="warning"
              notificationDescription="Warning - Importing a CSV will overwrite all existing entries. This cannot be undone."
            />
          </Flex>
          <Flex justifyContent={"center"}>
            <Box width="100%" tabIndex={-1}>
              <FileUpload
                secondaryText="Only CSV format is supported"
                primaryText="Select a file or drag and drop here"
                accept="text/csv"
                multiple={false}
                onUpload={handleUpload}
                data-testid="Import CSV"
                disabled={!loaded}
                aria-label="Import CSV File"
                dragAndDrop={true}
                autoUpload={true}
                clearOnUpload={true}
              />
            </Box>
          </Flex>
          {CSVErrors.length > 0 && (
            <Box>
              <Flex direction="column">
                <Flex pt="sp16">
                  <Text>{t(`There are problem(s) with your CSV import, ${CSVErrors.length} issues identified:`)}</Text>
                </Flex>
                <Box
                  sx={
                    CSVErrors.length > 3
                      ? { overflowY: "scroll", maxHeight: "325px", border: "1px solid #eee", borderRadius: "4px" }
                      : { maxHeight: "325px" }
                  }
                >
                  {CSVErrors.map((error) => (
                    <Flex p="6" key={error.line}>
                      <Text>{t(`Line ${error.line}: ${error.entry} - ${error.error}`)}</Text>
                    </Flex>
                  ))}
                </Box>
              </Flex>
              <Flex direction="row" justifyContent="space-between" pt="sp16">
                <Box mr="sp8">
                  <Button variant="danger" onClick={handleDownloadErrors}>
                    {t("Export Errors")}
                  </Button>
                </Box>
                <Flex>
                  <Button onClick={onClose} variant="primary" aria-label="close">
                    {t("Close")}
                  </Button>
                </Flex>
              </Flex>
            </Box>
          )}
        </Flex>
      </Box>
    </Modal>
  );
};

export default ImportCSVModal;
